import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import datasetsAPI from '~/api/desktop/datasets'
import Dialog from '~/components/Dialog'
import Notification from '~/components/notifications/Notification'
import Tabs from '~/components/Tabs'
import Dataset from './Dataset.interface'
import cs from './dataset_viewer_dialog.scss'

interface DatasetViewerDialogProps {
  isOpen: boolean
  onClose: () => void
  datasetId?: number
}

const DatasetViewerDialog = ({
  isOpen,
  onClose,
  datasetId,
}: DatasetViewerDialogProps) => {
  const [dataset, setDataset] = useState<Dataset | null>(null)
  const [rawData, setRawData] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState('structured_data')

  const fetchData = async () => {
    if (isOpen && datasetId) {
      const [datasetResponse, rawDataResponse] = await Promise.all([
        datasetsAPI.getDataset(datasetId, {
          verbose: '1',
        }),
        datasetsAPI.getDatasetRawData(datasetId),
      ])
      setRawData(rawDataResponse.rawData)
      setDataset(datasetResponse)
    }
    if (!isOpen) {
      setRawData(null)
      setDataset(null)
      // Tabs are unmounted and the tab is set back to structured_data.
      setCurrentTab('structured_data')
    }
  }
  useEffect(() => {
    fetchData()
  }, [datasetId, isOpen])

  if (!dataset) {
    return null
  }

  const renderStructuredData = () => {
    if (dataset.status === 'invalid') {
      return (
        <Notification
          type='error'
          className={cs.desktopNotification}
          label={
            <>
              The dataset file was invalid.
              <br />
              Error: {dataset.errorMessage}
              <br />
            </>
          }
        />
      )
    } else {
      return (
        <pre className={cs.data}>
          {JSON.stringify(dataset.structuredData, undefined, 2)}
        </pre>
      )
    }
  }

  const renderRawData = () => {
    return <pre className={cs.data}>{rawData}</pre>
  }

  const renderMetadata = () => {
    return (
      <pre className={cs.data}>{JSON.stringify(dataset.metadata, undefined, 2)}</pre>
    )
  }

  const tabs = [
    {
      key: 'structured_data',
      title: 'Structured Data',
      panel: renderStructuredData(),
    },
    {
      key: 'raw_data',
      title: 'Raw Data',
      panel: renderRawData(),
    },
    {
      key: 'metadata',
      title: 'Metadata',
      panel: renderMetadata(),
    },
  ]

  return (
    <Dialog isOpen={isOpen} onClose={onClose} className={cs.datasetViewerDialog}>
      <div className={cs.title}>{dataset.name}</div>
      <Tabs
        tabs={tabs}
        initialTab='structured_data'
        className={cs.tabs}
        panelClassName={cs.tabPanel}
        onTabChange={setCurrentTab}
        selectedTabId={currentTab}
      />
    </Dialog>
  )
}

DatasetViewerDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  datasetId: PropTypes.number,
}

export default DatasetViewerDialog
